import React, {useState, useEffect} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../AuthContext";
import {httpsCallable} from "firebase/functions";
import {functions} from "../../services/firebase/config";

const VerifyEmailToken: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState<
    "processing" | "success" | "error" | "invalid"
  >("processing");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const navigate = useNavigate();
  const {user, loading, refreshVerificationStatus} = useAuth();

  // Cloud Function for verifying email tokens
  const verifyEmailTokenFunction = httpsCallable(functions, "verifyEmailToken");

  useEffect(() => {
    const token = searchParams.get("token");

    console.log(
      "URL search params:",
      Object.fromEntries(searchParams.entries())
    );
    console.log(
      "Extracted token:",
      token ? token.substring(0, 8) + "..." : "No token found"
    );

    if (!token) {
      setVerificationStatus("invalid");
      setErrorMessage("No verification token provided in the URL.");
      return;
    }

    // Verify the token
    verifyToken(token);
  }, [searchParams]);

  const verifyToken = async (token: string) => {
    try {
      setVerificationStatus("processing");

      console.log("Attempting to verify token:", token.substring(0, 8) + "...");

      const result = await verifyEmailTokenFunction({token});
      const data = result.data as {success: boolean; message?: string};

      console.log("Verification result:", data);

      if (data.success) {
        setVerificationStatus("success");

        // If user is logged in, refresh their verification status and wait for it to complete
        if (user && !loading) {
          try {
            console.log("Refreshing user verification status...");
            await refreshVerificationStatus();
            console.log("User verification status refreshed successfully");

            // Wait a moment for the auth state to fully update
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Now redirect to dashboard
            navigate("/overview");
          } catch (error) {
            // Non-critical error - verification still succeeded, but redirect anyway
            console.warn("Failed to refresh user verification status:", error);
            navigate("/overview");
          }
        } else {
          // No user logged in, redirect immediately
          navigate("/overview");
        }
      } else {
        setVerificationStatus("error");
        setErrorMessage(data.message || "Verification failed");
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      setVerificationStatus("error");

      // Handle specific error codes from the cloud function
      if (error.code === "functions/not-found") {
        setErrorMessage(
          "Invalid or expired verification token. Please request a new verification email."
        );
      } else if (error.code === "functions/already-exists") {
        setErrorMessage(
          "This email is already verified. You can sign in normally."
        );
      } else if (error.code === "functions/permission-denied") {
        setErrorMessage("This verification token is not valid.");
      } else if (error.code === "functions/unavailable") {
        setErrorMessage(
          "Verification service is temporarily unavailable. Please try again later."
        );
      } else {
        setErrorMessage(
          error.message ||
            "An error occurred during verification. Please try again."
        );
      }
    }
  };

  const handleContinueToLogin = () => {
    // After successful verification, always go to dashboard
    navigate("/overview");
  };

  const handleRequestNewVerification = () => {
    navigate("/auth/email-verification");
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Email Verification</h1>

        {verificationStatus === "processing" && (
          <div className="verification-processing">
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            <h2>Verifying Your Email</h2>
            <p>Please wait while we verify your email address...</p>
          </div>
        )}

        {verificationStatus === "success" && (
          <div className="verification-success">
            <div className="success-icon">✓</div>
            <h2>Email Verified Successfully!</h2>
            <p>
              Your email address has been verified. You can now access your
              account.
            </p>
            <p>Redirecting to dashboard...</p>

            <div className="verification-actions">
              <button
                className="btn btn-primary"
                onClick={handleContinueToLogin}
              >
                Go to Dashboard Now
              </button>
            </div>
          </div>
        )}

        {(verificationStatus === "error" ||
          verificationStatus === "invalid") && (
          <div className="verification-error">
            <div className="error-icon">✗</div>
            <h2>Verification Failed</h2>
            <p>{errorMessage}</p>

            <div className="verification-actions">
              <button
                className="btn btn-primary"
                onClick={handleContinueToLogin}
              >
                Go to Sign In
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleRequestNewVerification}
              >
                Request New Verification
              </button>
            </div>

            <div className="verification-tips">
              <h3>Common Issues:</h3>
              <ul>
                <li>Verification links can only be used once</li>
                <li>Links expire after 24 hours</li>
                <li>Make sure you clicked the complete link from your email</li>
                <li>
                  If the link is broken, try requesting a new verification email
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailToken;
